import { useEffect, useState } from 'react'
import { cn } from '@duck/ui'

const SIZE = 132
const STROKE = 9
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

/** Maps a grade (as a 0-1 fraction of `max`) to the theme color the ring renders in. */
const getGradeColor = (fraction: number): string => {
  if (fraction >= 0.85) return 'text-success'
  if (fraction >= 0.7) return 'text-primary'
  return 'text-warning'
}

/** Animates 0 → target with an ease-out cubic. */
const useCountUp = (target: number, duration = 1100): number => {
  const [value, setValue] = useState(0)

  useEffect(() => {
    let raf = 0
    let start: number | null = null
    const tick = (now: number) => {
      if (start === null) start = now
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(eased * target)
      if (progress < 1) raf = requestAnimationFrame(tick)
      else setValue(target)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])

  return value
}

interface ScoreRingProps {
  grade: number
  /** Eyebrow label above the ring, e.g. "Your Score". */
  label: string
  /** Scale of the grade. Feedback grades are out of 10; the reliability score is out of 100. */
  max?: 10 | 100
  className?: string
}

export const ScoreRing = ({ grade, label, max = 10, className }: ScoreRingProps) => {
  const color = getGradeColor(grade / max)
  const animated = useCountUp(grade)

  // Defer the ring fill to the next frame so the dash transition runs from empty.
  const [filled, setFilled] = useState(false)
  useEffect(() => {
    const raf = requestAnimationFrame(() => setFilled(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  const pct = Math.max(0, Math.min(1, grade / max))
  const dashoffset = filled ? CIRCUMFERENCE * (1 - pct) : CIRCUMFERENCE
  const display = Number.isInteger(grade) ? Math.round(animated).toString() : animated.toFixed(1)

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </span>

      {/* Pure data viz: keep LTR so the ring isn't mirrored and the score reads "9/10" in RTL too. */}
      <div className="relative mt-3" dir="ltr" style={{ width: SIZE, height: SIZE }}>
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="-rotate-90"
        >
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            strokeWidth={STROKE}
            stroke="currentColor"
            strokeOpacity={0.35}
            className="text-border"
          />
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            strokeWidth={STROKE}
            strokeLinecap="round"
            stroke="currentColor"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashoffset}
            className={cn(color, 'transition-[stroke-dashoffset] duration-1000 ease-out')}
          />
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="flex items-baseline gap-0.5">
            <span className={cn('text-4xl font-bold leading-none tabular-nums', color)}>
              {display}
            </span>
            <span className="text-sm font-medium text-muted-foreground">/{max}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
